import { Download, Search } from "lucide-react";
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  readApplicantSearchListState,
  writeApplicantSearchListState,
} from '../utils/applicantSearchListStorage';

import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { useStore } from '../store';
import { SEARCH_CANDIDATES, SEND_CALENDAR_LINK } from '../lib/urls';

import ApplicantAnalysisResult from '../components/CandidateManagement/ApplicantAnalysisResult';
import { toAnalysisApplicant } from '../components/CandidateManagement/atsApplications';
import { useCandidateTranscript } from '../hooks/useCandidateTranscript';
import { isDevEnvironment } from '../utils/envUtils';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import { SmallText, XsmallText } from '../lib/ui/components/typography/Typography';
import Button from '../lib/ui/components/button/Button';
import Checkbox from '../lib/ui/components/checkbox/Checkbox';
import Table from '../lib/ui/components/table/Table';
import Badge from '../lib/ui/components/badge/Badge';
import MatchRating from '../lib/ui/components/match-rating/MatchRating';
import Link from '../lib/ui/components/link/Link';
import Spinner from '../lib/ui/components/spinner/Spinner';
import Dropdown from '../lib/ui/components/dropdown/Dropdown';
import Modal from '../lib/ui/components/Modal';
import MatchRatingFilter from '../lib/ui/components/match-rating-filter/MatchRatingFilter';
import RequiredQuestionsFilter from '../lib/ui/components/required-questions-filter/RequiredQuestionsFilter';
import PageHeader from '../components/Navigation/PageHeader';

import { useTablePagination } from '../hooks/useTablePagination';
import { formatStatus, getDisplayStatus } from '../components/CandidateManagement/StatusHelper';
import { formatPhoneNumber } from '../utils/phoneUtils';
import { bulkExportAnalysisToPDF } from '../components/CandidateManagement/ExportAnalysisHelper';
import {
  getRequiredQuestionsFilterValue,
  getRequiredQuestionsLabel,
} from '../utils/analysisDisplay';

const DOWNLOAD_LIMIT = 20;

function ApplicantSearchPage() {
  const company = useStore((state) => state.company);
  const token = useStore((state) => state.token);
  const devModeEnabled = useStore((state) => state.devModeEnabled);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);

  const [viewMode, setViewMode] = useState('list');
  const [selectedApplicantForAnalysis, setSelectedApplicantForAnalysis] = useState(null);
  const {
    isTranscriptLoading,
    transcriptError,
    retryTranscript,
    resetTranscript,
  } = useCandidateTranscript({
    enabled: viewMode === 'analysis',
    applicant: selectedApplicantForAnalysis,
    setApplicant: setSelectedApplicantForAnalysis,
    companyId: company?.id,
    jobId: selectedApplicantForAnalysis?.job?.id,
    token,
  });

  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingCalendar, setIsSendingCalendar] = useState(false);
  const [calendarLinkSentIds, setCalendarLinkSentIds] = useState(new Set());
  const [excerptModal, setExcerptModal] = useState({ name: '', text: '' });

  const [matchRatingFilter, setMatchRatingFilter] = useState(
    new Set([1, 2, 3, 4, 5, "No Rating"])
  );
  const [requiredQuestionsFilter, setRequiredQuestionsFilter] = useState(
    new Set(["Passed", "Needs review", "NA"])
  );
  
  const [cacheRestored, setCacheRestored] = useState(false);
  const isRestoringCache = useRef(false);
  const pendingSearchRestoreRef = useRef(null);

  const filteredApplicants = useMemo(() => {
    if (!searchResults || !searchResults.results) return [];

    let filtered = searchResults.results.filter((applicant) => {
      const rating =
        applicant?.interview_session?.ai_insights?.overall_match_rating;
      // If no rating, check if No Rating is selected
      if (rating === undefined || rating === null) {
        return matchRatingFilter.has("No Rating");
      }
      // Check if rating is in the selected set
      return matchRatingFilter.has(rating);
    });

    // Filter by required questions status
    return filtered.filter((applicant) => {
      const status =
        applicant?.interview_session?.ai_insights?.required_questions_status;
      return requiredQuestionsFilter.has(getRequiredQuestionsFilterValue(status));
    });
  }, [searchResults, matchRatingFilter, requiredQuestionsFilter]);

  const {
    currentPage,
    itemsPerPage,
    totalPages,
    currentItems: currentApplicants,
    sortedItems: sortedApplicants,
    sortField,
    sortDirection,
    handlePageChange,
    handleSort,
    setCurrentPage,
    setSortField,
    setSortDirection
  } = useTablePagination(filteredApplicants, {
    itemsPerPage: 10,
    defaultSortField: 'relevance_score',
    defaultSortDirection: 'desc',
    sortFieldConfig: {
      relevance_score: {
        type: 'number',
        getValue: (item) => item?.relevance_score,
        nullHandling: 'bottom'
      },
      match_rating: {
        type: 'number',
        getValue: (item) => item?.interview_session?.ai_insights?.overall_match_rating,
        nullHandling: 'bottom'
      },
      required_questions_status: {
        type: 'string',
        getValue: (item) => {
          const status = item?.interview_session?.ai_insights?.required_questions_status;
          return getRequiredQuestionsLabel(status);
        },
        nullHandling: 'bottom'
      },
      name: {
        type: 'string',
        getValue: (item) => item.name
      },
      updated_at: {
        type: 'date',
        getValue: (item) => item?.interview_session?.updated_at,
        nullHandling: 'bottom'
      },
      created_at: {
        type: 'date',
        getValue: (item) => item?.created_at,
        nullHandling: 'bottom'
      },
      job_role: {
        type: 'string',
        getValue: (item) => item?.job?.role_name || ''
      },
      status: {
        type: 'string',
        getValue: (item) => getDisplayStatus(item)
      }
    }
  });

  useEffect(() => {
    if (!isRestoringCache.current) {
      setCurrentPage(1);
    }
  }, [matchRatingFilter, setCurrentPage]);

  const hasResults = sortedApplicants?.length > 0;

  useEffect(() => {
    if (!cacheRestored && company?.id) {
      const saved = readApplicantSearchListState(company.id);
      if (saved) {
        isRestoringCache.current = true;
        setQuery(saved.query || '');
        setMatchRatingFilter(new Set(saved.matchRatingFilter));
        setRequiredQuestionsFilter(new Set(saved.requiredQuestionsFilter));
        if (saved.query?.trim()) {
          pendingSearchRestoreRef.current = {
            searchQuery: saved.query.trim(),
            restoreAfter: {
              currentPage: saved.currentPage,
              sortField: saved.sortField,
              sortDirection: saved.sortDirection,
              selectedApplicantIds: saved.selectedApplicantIds,
            },
          };
        } else {
          isRestoringCache.current = false;
        }
      }
      setCacheRestored(true);
    }
  }, [cacheRestored, company]);

  useEffect(() => {
    if (cacheRestored && company?.id) {
      writeApplicantSearchListState(company.id, {
        query,
        matchRatingFilter: Array.from(matchRatingFilter),
        requiredQuestionsFilter: Array.from(requiredQuestionsFilter),
        selectedApplicantIds: Array.from(selectedApplicants),
        currentPage,
        sortField,
        sortDirection,
      });
    }
  }, [
    cacheRestored,
    company,
    query,
    matchRatingFilter,
    requiredQuestionsFilter,
    selectedApplicants,
    currentPage,
    sortField,
    sortDirection,
  ]);

  const runSearch = useCallback(
    async ({ searchQuery, restoreAfter = null } = {}) => {
      const trimmed = (searchQuery ?? query).trim();
      if (!trimmed) {
        toast.error('Please enter a search query');
        return;
      }

      if (!company?.id) {
        toast.error('Company not found');
        return;
      }

      setIsSearching(true);
      setError(null);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      try {
        const response = await fetch(SEARCH_CANDIDATES(company.id), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ query: trimmed }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Search failed');
        }

        const data = await response.json();
        setSearchResults(data);

        if (restoreAfter) {
          if (restoreAfter.currentPage) {
            setCurrentPage(restoreAfter.currentPage);
          }
          if (restoreAfter.sortField) {
            setSortField(restoreAfter.sortField);
          }
          if (restoreAfter.sortDirection) {
            setSortDirection(restoreAfter.sortDirection);
          }
          setSelectedApplicants(
            new Set(restoreAfter.selectedApplicantIds || [])
          );
        } else {
          setSelectedApplicants(new Set());
          setCurrentPage(1);
        }
      } catch (err) {
        console.error('Search error:', err);
        if (err.name === 'AbortError') {
          setError('Search is taking too long. Please try again.');
          toast.error('Search is taking too long. Please try again.');
        } else {
          setError(
            err.message ||
              'Search failed. Please try again or refine your query.'
          );
          toast.error(
            err.message ||
              'Search failed. Please try again or refine your query.'
          );
        }
      } finally {
        setIsSearching(false);
        clearTimeout(timeoutId);
      }
    },
    [query, company, token, setCurrentPage, setSortField, setSortDirection]
  );

  useEffect(() => {
    if (!cacheRestored || !company?.id || !pendingSearchRestoreRef.current) {
      return;
    }
    const pending = pendingSearchRestoreRef.current;
    pendingSearchRestoreRef.current = null;
    runSearch(pending).finally(() => {
      isRestoringCache.current = false;
    });
  }, [cacheRestored, company?.id, runSearch]);

  const handleSearch = useCallback(() => {
    runSearch({ searchQuery: query });
  }, [query, runSearch]);

  const navigateToAnalysis = useCallback((localIndex) => {
    const globalIndex = (currentPage - 1) * itemsPerPage + localIndex;
    const applicant = sortedApplicants[globalIndex];
    
    resetTranscript();
    setSelectedApplicantForAnalysis(toAnalysisApplicant(applicant));
    setViewMode('analysis');
  }, [sortedApplicants, currentPage, itemsPerPage, resetTranscript]);

  const navigateBackToList = useCallback(() => {
    setViewMode('list');
    setSelectedApplicantForAnalysis(null);
    resetTranscript();
  }, [resetTranscript]);

  const downloadableApplicants = useMemo(() => {
    return filteredApplicants.filter(app => 
      app?.interview_session?.status !== 'PENDING' &&
      app?.interview_session?.status !== 'NOT_SENT' &&
      app?.status !== 'NEW'
    );
  }, [filteredApplicants]);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const downloadableIds = downloadableApplicants.map(app => app.id);
      setSelectedApplicants(new Set(downloadableIds));
    } else {
      setSelectedApplicants(new Set());
    }
  }, [downloadableApplicants]);

  const handleSelectApplicant = useCallback((id, checked) => {
    const newSelected = new Set(selectedApplicants);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedApplicants(newSelected);
  }, [selectedApplicants]);

  const getCandidatesWithCompletedAnalysis = useCallback((candidateIds) => {
    return candidateIds.filter(id => {
      const candidate = filteredApplicants.find(app => app.id === id);
      return candidate &&
        candidate.status !== 'NEW' &&
        candidate.interview_session &&
        candidate.interview_session.status !== 'PENDING' &&
        candidate.interview_session.status !== 'NOT_SENT';
    });
  }, [filteredApplicants]);

  const isOverDownloadLimit = useCallback(() => {
    return getCandidatesWithCompletedAnalysis(Array.from(selectedApplicants)).length > DOWNLOAD_LIMIT;
  }, [selectedApplicants, getCandidatesWithCompletedAnalysis]);

  const handleBulkDownloadAnalysis = useCallback(async () => {
    const eligibleCandidates = getCandidatesWithCompletedAnalysis(Array.from(selectedApplicants));

    if (eligibleCandidates.length === 0) {
      toast.error('No eligible applicants selected for download');
      return;
    }

    setIsDownloading(true);

    try {
      const candidatesData = eligibleCandidates.map(id => {
        const candidate = filteredApplicants.find(app => app.id === id);
        return {
          name: candidate.name,
          phone: candidate.phone_number,
          status: candidate.interview_session?.status,
          analysis_status: candidate.interview_session?.analysis_status,
          created_at: candidate.interview_session?.created_at,
          updated_at: candidate.interview_session?.updated_at,
          ai_insights: candidate.interview_session?.ai_insights,
          interview_session_id: candidate.interview_session?.id,
          interview_responses: candidate.interview_session?.interview_responses || [],
          is_closed: candidate.interview_session?.is_closed,
          job: candidate.job
        };
      });

      await bulkExportAnalysisToPDF(candidatesData, company.name);
      toast.success(`Successfully downloaded ${candidatesData.length} evaluation reports`);
      setSelectedApplicants(new Set());

    } catch (error) {
      console.error('Error downloading analysis:', error);
      toast.error('Failed to download evaluation reports');
    } finally {
      setIsDownloading(false);
    }
  }, [selectedApplicants, filteredApplicants, company, getCandidatesWithCompletedAnalysis]);

  const sendCalendarLinkToSingle = useCallback(async (applicant) => {
    if (!applicant?.id || !applicant?.job_id || !company?.id) {
      toast.error("Unable to send calendar link");
      return;
    }

    setIsSendingCalendar(true);

    try {
      const response = await fetch(SEND_CALENDAR_LINK(company.id, applicant.job_id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: [applicant.id],
        }),
      });

      if (response.ok) {
        setCalendarLinkSentIds((prev) => new Set(prev).add(applicant.id));
        toast.success(`Calendar link sent to ${applicant.name}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send calendar link");
      }
    } catch (error) {
      console.error("Error sending calendar link:", error);
      toast.error(error.message || "Failed to send calendar link");
    } finally {
      setIsSendingCalendar(false);
    }
  }, [company, token]);

  const formatLastUpdated = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }, []);

  const handleDownloadCsv = useCallback(() => {
    if (!sortedApplicants || sortedApplicants.length === 0) return;

    try {
      const headers = ['Applicant', 'Match Rating', 'Pass/Fail', 'Role', 'Status', 'Phone Number', 'Last Updated'];
      const rows = sortedApplicants.map(applicant => {
        const matchRating = typeof applicant?.interview_session?.ai_insights?.overall_match_rating === 'number'
          ? applicant.interview_session.ai_insights.overall_match_rating
          : '-';
        const roleName = applicant?.job?.role_name || '-';
        const statusLabel = formatStatus(getDisplayStatus(applicant));
        const phoneValue = formatPhoneNumber(applicant?.phone_number);
        const updatedValue = formatLastUpdated(applicant?.interview_session?.updated_at);
        const applicantName = applicant?.name || '-';
        
        // Format required questions status
        const requiredQuestionsStatus = getRequiredQuestionsLabel(
          applicant?.interview_session?.ai_insights?.required_questions_status
        );

        const values = [applicantName, matchRating, requiredQuestionsStatus, roleName, statusLabel, phoneValue, updatedValue];

        return values.map(item => {
          const stringValue = item === null || item === undefined ? '' : String(item);
          if (/[",\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
      });

      const csvString = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `applicant-search-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV file');
    }
  }, [sortedApplicants, formatLastUpdated]);

  const canDownloadApplicantAnalysis = useCallback((applicant) => {
    if (!applicant) return false;
    if (!applicant.interview_session) return false;
    if (applicant.status === 'NEW') return false;
    const sessionStatus = applicant.interview_session.status;
    return sessionStatus !== 'PENDING' && sessionStatus !== 'NOT_SENT';
  }, []);

  const handleDownloadApplicantAnalysis = useCallback(async (applicant) => {
    if (!canDownloadApplicantAnalysis(applicant)) {
      toast.error('Evaluation not available for this applicant');
      return;
    }

    setIsDownloading(true);

    try {
      const candidateData = {
        name: applicant.name,
        phone: applicant.phone_number,
        status: applicant.interview_session?.status,
        analysis_status: applicant.interview_session?.analysis_status,
        created_at: applicant.interview_session?.created_at,
        updated_at: applicant.interview_session?.updated_at,
        ai_insights: applicant.interview_session?.ai_insights,
        interview_session_id: applicant.interview_session?.id,
        interview_responses: applicant.interview_session?.interview_responses || [],
        is_closed: applicant.interview_session?.is_closed,
        job: applicant.job
      };

      await bulkExportAnalysisToPDF([candidateData], company?.name || '');
    } catch (error) {
      console.error('Error downloading analysis:', error);
      toast.error('Failed to download evaluation report');
    } finally {
      setIsDownloading(false);
    }
  }, [company, canDownloadApplicantAnalysis]);

  if (viewMode === 'analysis' && selectedApplicantForAnalysis) {
    return (
      <ErrorBoundary
        title="Evaluation Error"
        description="Error loading evaluation view."
      >
        <ApplicantAnalysisResult
          selectedApplicantForAnalysis={selectedApplicantForAnalysis}
          navigateBackToList={navigateBackToList}
          onCalendarLinkSent={(sentAt) => {
            setSelectedApplicantForAnalysis((prev) =>
              prev ? { ...prev, calendar_link_sent_at: sentAt } : prev
            );
          }}
          roleName={selectedApplicantForAnalysis?.job?.role_name || ''}
          companyName={company?.name || ''}
          companyId={company?.id}
          jobId={selectedApplicantForAnalysis?.job?.id}
          hasAnalysisRubric={
            selectedApplicantForAnalysis?.job?.has_analysis_criteria
          }
          token={token}
          isTranscriptLoading={isTranscriptLoading}
          transcriptError={transcriptError}
          onRetryTranscript={retryTranscript}
          onAnalysisFeedbackSaved={(payload) => {
            setSelectedApplicantForAnalysis((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                ai_insights: {
                  ...(prev.ai_insights || {}),
                  analysis_liked: payload.analysis_liked,
                  analysis_comment: payload.analysis_comment,
                },
              };
            });
          }}
        />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      title="Applicant Search Error"
      description="Error loading applicant search page."
    >
      <div>
        <PageHeader
          title="Applicant Search"
          actions={(
            <Dropdown
              placement="bottom-end"
              items={[{
                label: 'Download CSV',
                onClick: handleDownloadCsv,
                disabled: !hasResults
              }]}
            />
          )}
        />
        <div className="homepage">
          <div className="homepage-body">
            <div className="homepage-body-inner">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 mx-4 mt-6">
                <div className="mb-6">
                  <SmallText weight="bold" className="text-gray-600">
                    Search previous interviews for applicants that match what you're looking for.
                  </SmallText>
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder='ex. "Which candidates were open to a hybrid work schedule?"'
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={isSearching}
                  />
                  <Button
                    onClick={handleSearch}
                    disabled={isSearching || !query.trim()}
                    leftIcon={isSearching ? <Spinner size="sm" /> : <Search size="1em" />}
                    variant="primary"
                  >
                    {isSearching ? 'Searching...' : 'Search Applicants'}
                  </Button>
                </div>
              </div>

              {isSearching && (
                <div className="mx-4 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                  <Table className="min-w-full">
                    <Table.Head>
                      <Table.HeadCell />
                      <Table.HeadCell>Applicant</Table.HeadCell>
                      <Table.HeadCell>Match Rating</Table.HeadCell>
                      <Table.HeadCell>Pass/Fail</Table.HeadCell>
                      <Table.HeadCell>Status</Table.HeadCell>
                      <Table.HeadCell className="w-[50px]" />
                    </Table.Head>
                    <Table.Body loading skeletonRowCount={5} />
                  </Table>
                </div>
              )}

              {error && !isSearching && (
                <div className="text-center py-12 px-4">
                  <SmallText className="text-red-600">{error}</SmallText>
                </div>
              )}

              {!isSearching && !error && !searchResults && (
                <div className="text-center py-12 px-4">
                  <SmallText className="text-gray-500">
                    Enter a search query to find matching candidates.
                  </SmallText>
                </div>
              )}

              {!isSearching && searchResults && searchResults.results.length === 0 && (
                <div className="text-center py-12 px-4">
                  <SmallText className="text-gray-600">
                    No candidates found matching your query. Try a different search.
                  </SmallText>
                </div>
              )}

              {!isSearching && searchResults && searchResults.results.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mx-4">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <SmallText weight="bold">
                          {filteredApplicants.length} Result{filteredApplicants.length !== 1 ? 's' : ''}
                        </SmallText>
                        {searchResults.query_summary && (
                          <XsmallText className="text-gray-600 mt-1">
                            {searchResults.query_summary}
                          </XsmallText>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <MatchRatingFilter
                          value={matchRatingFilter}
                          onChange={setMatchRatingFilter}
                        />
                        <RequiredQuestionsFilter
                          value={requiredQuestionsFilter}
                          onChange={setRequiredQuestionsFilter}
                        />
                      </div>
                    </div>
                  </div>

                  {filteredApplicants.length > 0 && (
                    <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="select-all"
                            checked={selectedApplicants.size === downloadableApplicants.length && downloadableApplicants.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                          <XsmallText
                            weight="bold"
                            className="cursor-pointer"
                            onClick={() => {
                              const checkbox = document.getElementById('select-all');
                              if (checkbox) checkbox.click();
                            }}
                          >
                            {selectedApplicants.size === downloadableApplicants.length && downloadableApplicants.length > 0 ? 'Deselect All' : 'Select All'}
                          </XsmallText>
                        </div>
                        <SmallText>|</SmallText>
                        <XsmallText weight="bold">{selectedApplicants.size} selected:</XsmallText>
                        <Button
                          variant="tertiary"
                          size="sm"
                          leftIcon={<Download size="1em" />}
                          onClick={handleBulkDownloadAnalysis}
                          disabled={selectedApplicants.size === 0 || isDownloading || getCandidatesWithCompletedAnalysis(Array.from(selectedApplicants)).length === 0 || isOverDownloadLimit()}
                          title={isOverDownloadLimit() ? "Exceeds limit" : ""}
                        >
                          {isDownloading ? 'Downloading...' : 'Download Evaluation'}
                        </Button>
                      </div>
                    </div>
                  )}

                  <Table
                    paginated={true}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                  >
                    <Table.Head>
                      <Table.HeadCell></Table.HeadCell>
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'name' ? sortDirection : null}
                        onSort={() => handleSort('name')}
                      >
                        Applicant
                      </Table.HeadCell>
                      {/* <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'relevance_score' ? sortDirection : null}
                        onSort={() => handleSort('relevance_score')}
                      >
                        Relevance
                      </Table.HeadCell> */}
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'match_rating' ? sortDirection : null}
                        onSort={() => handleSort('match_rating')}
                      >
                        Match Rating
                      </Table.HeadCell>
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'required_questions_status' ? sortDirection : null}
                        onSort={() => handleSort('required_questions_status')}
                      >
                        Pass/Fail
                      </Table.HeadCell>
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'job_role' ? sortDirection : null}
                        onSort={() => handleSort('job_role')}
                      >
                        Role
                      </Table.HeadCell>
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'status' ? sortDirection : null}
                        onSort={() => handleSort('status')}
                      >
                        Status
                      </Table.HeadCell>
                      <Table.HeadCell>Phone Number</Table.HeadCell>
                      <Table.HeadCell>Calendar Link</Table.HeadCell>
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'created_at' ? sortDirection : null}
                        onSort={() => handleSort('created_at')}
                      >
                        Created Date
                      </Table.HeadCell>
                      <Table.HeadCell
                        sortable={true}
                        sortDirection={sortField === 'updated_at' ? sortDirection : null}
                        onSort={() => handleSort('updated_at')}
                      >
                        Last Updated
                      </Table.HeadCell>
                      <Table.HeadCell className="w-[50px]"></Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {currentApplicants.map((applicant, index) => {
                        const displayStatus = getDisplayStatus(applicant);
                        return (
                          <Table.Row key={applicant.id} className="bg-white">
                            <Table.Cell>
                              <Checkbox
                                id={`select-${applicant.id}`}
                                checked={selectedApplicants.has(applicant.id)}
                                onChange={(e) => handleSelectApplicant(applicant.id, e.target.checked)}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Link
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigateToAnalysis(index);
                                }}
                                disabled={
                                  applicant?.interview_session?.status === "NOT_SENT" ||
                                  applicant?.status === "NEW" ||
                                  (applicant?.interview_session?.status === "PENDING" &&
                                    !(isDevEnvironment() && devModeEnabled))
                                }
                              >
                                {applicant.name}
                              </Link>
                              {applicant?.search_result_excerpt && (
                                <XsmallText
                                  className="text-gray-600 truncate max-w-[320px] cursor-pointer"
                                  title={applicant.search_result_excerpt}
                                  onClick={() =>
                                    setExcerptModal({
                                      name: applicant.name ?? '',
                                      text: applicant.search_result_excerpt,
                                    })
                                  }
                                >
                                  {applicant.search_result_excerpt}
                                </XsmallText>
                              )}
                            </Table.Cell>
                            {/* <Table.Cell>
                              <SmallText weight="semibold">{applicant.relevance_score || '-'}</SmallText>
                            </Table.Cell> */}
                            <Table.Cell>
                              {applicant?.interview_session?.ai_insights?.overall_match_rating ? (
                                <MatchRating rating={applicant.interview_session.ai_insights.overall_match_rating} />
                              ) : (
                                <XsmallText>-</XsmallText>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <XsmallText weight="normal">
                                {getRequiredQuestionsLabel(
                                  applicant?.interview_session?.ai_insights
                                    ?.required_questions_status
                                )}
                              </XsmallText>
                            </Table.Cell>
                            <Table.Cell>
                              {applicant?.job ? (
                                <Link
                                  to={`/jobs/${encodeURIComponent(applicant.job.role_name)}/${applicant.job.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {applicant.job.role_name}
                                </Link>
                              ) : (
                                <XsmallText>-</XsmallText>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge variant={
                                displayStatus === 'completed' ? 'success' :
                                displayStatus === 'in_progress' ? 'info' :
                                displayStatus === 'closed' ? 'default' : 'warning'
                              }>
                                {formatStatus(displayStatus)}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              <XsmallText>{formatPhoneNumber(applicant.phone_number)}</XsmallText>
                            </Table.Cell>
                            <Table.Cell>
                              {applicant?.calendar_link_sent_at ||
                              calendarLinkSentIds.has(applicant.id) ? (
                                <Button
                                  variant="ghost"
                                  onClick={() =>
                                    sendCalendarLinkToSingle(applicant)
                                  }
                                  disabled={isSendingCalendar}
                                >
                                  Resend Calendar Link
                                </Button>
                              ) : (
                                <Button
                                  variant="secondary"
                                  onClick={() =>
                                    sendCalendarLinkToSingle(applicant)
                                  }
                                  disabled={isSendingCalendar}
                                >
                                  Send Calendar Link
                                </Button>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <XsmallText>
                                {applicant?.created_at
                                  ? new Date(applicant.created_at).toLocaleDateString("en-US")
                                  : "N/A"}
                              </XsmallText>
                            </Table.Cell>
                            <Table.Cell>
                              <XsmallText>
                                {formatLastUpdated(applicant?.interview_session?.updated_at)}
                              </XsmallText>
                            </Table.Cell>
                            <Table.Cell>
                              <Dropdown
                                placement="bottom-end"
                                items={[{
                                  label: 'Download Evaluation',
                                  onClick: () => handleDownloadApplicantAnalysis(applicant),
                                  disabled: !canDownloadApplicantAnalysis(applicant) || isDownloading
                                }]}
                              />
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={Boolean(excerptModal.text)}
        onClose={() => setExcerptModal({ name: '', text: '' })}
        size="2xl"
        dismissible
      >
        <Modal.Header>{excerptModal.name}</Modal.Header>
        <Modal.Body className="max-h-[70vh] overflow-y-auto">
          <SmallText className="whitespace-pre-wrap break-words">
            {excerptModal.text}
          </SmallText>
        </Modal.Body>
      </Modal>
    </ErrorBoundary>
  );
}

export default ApplicantSearchPage;
